import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Maximize2, MapPinned, Radio } from 'lucide-react';
import type { Incident } from '@duty/shared';
import { MAPBOX_TOKEN, MAPBOX_STYLES } from '@/config/mapbox';
import { useTheme } from '@/theme/theme';
import { useCoverage, emptyCoverageOverrides, type CoverageQuery } from '@/hooks/useCoverage';
import { candidateStationsNear, coverageAvailableFor, turnoutProfilesFor } from '@/incident/coverage';
import { CoverageAnalysisView } from './CoverageAnalysisView';
import { CoveragePlanningView } from './CoveragePlanningView';

const SOURCE_ID = 'response-order-stations';

/**
 * Header-level summary: a compact non-interactive map plus the fastest
 * estimated brigade and the nominal-area flag, with an expand affordance into
 * the full-screen coverage analysis view. See docs/response-coverage-2.0.md §5.2.
 */
export function ResponseCoverageBar({ incident }: { incident: Incident }) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [planningOpen, setPlanningOpen] = useState(false);
  const available = coverageAvailableFor(incident);
  const overrides = useMemo(() => emptyCoverageOverrides(), []);
  const stations = useMemo(
    () => (incident.coordinates ? candidateStationsNear(incident.coordinates, { maxRadiusM: 80_000, maxCount: 12 }) : []),
    [incident.coordinates?.lng, incident.coordinates?.lat],
  );
  const query = useMemo<CoverageQuery>(
    () => ({ stations, turnoutProfiles: turnoutProfilesFor(stations), incidentPoint: incident.coordinates }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stations, incident.coordinates?.lng, incident.coordinates?.lat],
  );
  const { responseOrder } = useCoverage(query, overrides);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current || !incident.coordinates) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAPBOX_STYLES[theme],
      center: [incident.coordinates.lng, incident.coordinates.lat],
      zoom: 10.5,
      interactive: false,
      attributionControl: false,
    });
    new mapboxgl.Marker({ color: '#f45454' })
      .setLngLat([incident.coordinates.lng, incident.coordinates.lat])
      .addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !responseOrder) return;
    const coordById = new Map(stations.map((s) => [s.id, s.coord]));
    const rankedWithCoords = responseOrder.ranked
      .filter((r) => r.rank !== undefined && coordById.has(r.stationId))
      .sort((a, b) => a.rank! - b.rank!);

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: rankedWithCoords.map((r) => {
        const coord = coordById.get(r.stationId)!;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [coord.lng, coord.lat] },
          properties: { rank: r.rank, fastest: r.rank === 1, stationId: r.stationId },
        };
      }),
    };
    const addStationMarkers = () => {
      if (!map.getLayer(`${SOURCE_ID}-circles`)) {
        map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });
        map.addLayer({
          id: `${SOURCE_ID}-circles`,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': ['case', ['get', 'fastest'], 6, 4],
            'circle-color': ['case', ['get', 'fastest'], '#22c55e', '#6b7280'],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#0b0f14',
          },
        });
      } else {
        (map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource).setData(geojson);
      }
    };
    if (map.isStyleLoaded()) addStationMarkers();
    else map.once('load', addStationMarkers);
  }, [responseOrder, stations]);

  if (!available) return null;

  const fastest = responseOrder?.ranked.find((r) => r.rank === 1);

  return (
    <>
      <div className="flex h-32 shrink-0 items-stretch gap-3 border-b border-border bg-surface/60 px-3 py-2">
        {MAPBOX_TOKEN && incident.coordinates ? (
          <div ref={containerRef} className="h-full w-40 shrink-0 overflow-hidden rounded-lg border border-border" aria-hidden />
        ) : (
          <div className="flex h-full w-40 shrink-0 items-center justify-center rounded-lg border border-border text-[10px] text-muted">
            Map unavailable
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
            <Radio size={11} /> Response coverage
          </div>
          <div className="truncate text-sm text-text">{incident.location}</div>
          {fastest ? (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted">Est. fastest:</span>
              <span className="font-semibold text-accent">
                {fastest.stationName} · {fastest.totalMinutes !== undefined ? `${Math.round(fastest.totalMinutes)} min` : '—'}
              </span>
              {responseOrder?.nominalOutranked && (
                <span className="chip !border-med/40 !bg-med/10 !text-med" title={responseOrder.explanation}>
                  ⚑ nominal outranked
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted">Computing estimated response order…</div>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-1.5 self-center">
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-xs text-muted hover:text-accent"
            aria-label="Expand response coverage analysis"
          >
            <Maximize2 size={13} /> Expand
          </button>
          <button
            onClick={() => setPlanningOpen(true)}
            className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-xs text-muted hover:text-accent"
            aria-label="Open coverage planning"
          >
            <MapPinned size={13} /> Planning
          </button>
        </div>
      </div>
      {expanded && <CoverageAnalysisView incident={incident} onClose={() => setExpanded(false)} />}
      {planningOpen && <CoveragePlanningView onClose={() => setPlanningOpen(false)} />}
    </>
  );
}
